/**
 * BuildAnAgent — multi-step self-service wizard (AA-207).
 *
 * Steps:
 *   1. Pick a template
 *   2. Name + description (auto-suggests an agentId)
 *   3. Knowledge source — SharePoint folder (skipped if template doesn't
 *      require one)
 *   4. Review + submit
 *
 * New self-service agents are always created Private. The owner can
 * later invite specific teammates from the My Agents card (which flips
 * the agent to Shared); promotion to "Published" (visible to everyone
 * in AskAldo) is reserved for AskAldo administrators and is not exposed
 * here. Removing the visibility step keeps the wizard focused and
 * removes the most common source of accidental over-sharing.
 *
 * Calls the new /api/me/agents endpoint, which itself routes through
 * requireAgentOwner on every owner-mutating route. The page navigates
 * to /my-agents on success.
 *
 * UX notes:
 *   - Templates section pulls from /api/agents/templates and renders a
 *     card grid. We display sample questions and the configured behavior
 *     so users can pick the right starting point.
 *   - The agentId is auto-suggested from the name but can be overridden;
 *     reserved/duplicate IDs are validated server-side.
 *   - The SharePoint picker searches tenant sites and browses the selected
 *     site's document library; membership is enforced server-side.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, AlertCircle, Sparkles, Loader2 } from 'lucide-react';
import {
  listTemplates,
  createMyAgent,
  suggestSampleQuestionsFromKB,
  checkFolderOverlap,
  AgentTemplate,
} from '../services/myAgents';
import SharePointFolderPicker, {
  type KnowledgeFolderSelection,
} from '../components/SharePointFolderPicker';

const STEP_LABELS = ['Template', 'Details', 'Knowledge', 'Routing', 'Review'] as const;

// Cap the number of sample questions we ask owners to author. Aldo's
// planner uses these as similarity targets — past ~6 the marginal
// signal drops fast and the wizard gets noisy.
const MAX_SAMPLE_QUESTIONS = 6;

const BuildAnAgent: React.FC = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [templates, setTemplates] = useState<AgentTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templatesError, setTemplatesError] = useState<string | null>(null);

  const [chosenTemplateId, setChosenTemplateId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [knowledgeSource, setKnowledgeSource] = useState<KnowledgeFolderSelection | null>(null);

  // Routing metadata — surfaced to Aldo's planner so direct questions
  // can be auto-routed to this agent. Pre-filled from the chosen
  // template's sampleQuestions; user may edit / extend / clear.
  const [sampleQuestions, setSampleQuestions] = useState<string[]>([]);
  const [routingTips, setRoutingTips] = useState('');
  // AA-210: intendedAudience was removed. Audience is now SharePoint
  // membership (and explicit grants), expressed via the Share tab on
  // the agent card after creation. The wizard no longer asks for a
  // free-text audience hint.

  // "Suggest from documents" status. We surface a one-line message
  // (success or failure) inline next to the button rather than as a
  // toast — it's a wizard, not a navigation surface, and the user
  // is looking right at the field they just asked us to fill.
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestNotice, setSuggestNotice] = useState<{
    kind: 'success' | 'info' | 'error';
    text: string;
  } | null>(null);

  // Tracks whether the user has manually edited any of the sample
  // questions (typed, added, or removed). Once true, the auto-
  // suggester stops overwriting their work — they can still trigger
  // a regenerate explicitly via the button. Reset only when the
  // user resets the wizard (i.e. on unmount/reset, which the parent
  // re-mounts the component for).
  const [questionsTouched, setQuestionsTouched] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    listTemplates()
      .then((tpls) => {
        if (!alive) return;
        setTemplates(tpls);
      })
      .catch((err) => {
        if (!alive) return;
        setTemplatesError(err.message || 'Failed to load templates');
      })
      .finally(() => {
        if (alive) setTemplatesLoading(false);
      });
    return () => { alive = false; };
  }, []);

  const chosenTemplate = useMemo(
    () => templates.find((t) => t.id === chosenTemplateId) || null,
    [templates, chosenTemplateId]
  );

  // Seed sample questions from the template the first time it's
  // chosen (or when the user switches templates while the field is
  // still pristine). We only auto-fill if the user hasn't typed
  // anything custom — otherwise switching templates would silently
  // wipe their work.
  useEffect(() => {
    if (!chosenTemplate) return;
    setSampleQuestions((prev) => {
      const isPristine =
        prev.length === 0 || prev.every((q) => q.trim().length === 0);
      if (!isPristine) return prev;
      const seeded = (chosenTemplate.sampleQuestions || []).slice(0, MAX_SAMPLE_QUESTIONS);
      return seeded.length > 0 ? seeded : prev;
    });
  }, [chosenTemplate]);

  const requiresKnowledge = chosenTemplate?.requiresKnowledgeSource === true;

  // Skip the Knowledge step if the chosen template doesn't need a source.
  const visibleSteps = useMemo(() => {
    if (!requiresKnowledge) return ['Template', 'Details', 'Routing', 'Review'];
    return STEP_LABELS;
  }, [requiresKnowledge]);

  const canAdvance = (() => {
    if (visibleSteps[step] === 'Template') return Boolean(chosenTemplateId);
    // The agentId is server-generated from the name (slug + 4-char
    // suffix in routes/myAgents.js _generateAgentId). Wizard no
    // longer exposes the slug to end users — name is the only
    // required field on this step.
    if (visibleSteps[step] === 'Details') return name.trim().length > 0;
    if (visibleSteps[step] === 'Knowledge') return true; // optional
    if (visibleSteps[step] === 'Routing') return true;   // optional
    if (visibleSteps[step] === 'Review') return true;
    return false;
  })();

  const handleNext = () => {
    if (step < visibleSteps.length - 1) setStep(step + 1);
  };
  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const cleanedSampleQuestions = useMemo(
    () => sampleQuestions.map((q) => q.trim()).filter((q) => q.length > 0),
    [sampleQuestions]
  );

  // The button is disabled until a knowledge source exists. Without
  // a SharePoint folder there's nothing for the backend to read
  // filenames from, so the suggestion would be no better than asking
  // the user to write the questions themselves.
  const canSuggestQuestions = !!knowledgeSource?.site && !suggestLoading;

  /**
   * Run the suggestion call and apply the result to the sample-
   * questions list. Shared by the explicit button click and the
   * auto-trigger effect, so they stay in sync on success / failure
   * messaging.
   *
   * `auto=true` distinguishes the silent auto-trigger from the
   * user-initiated click: in auto mode we suppress error notices
   * (the user didn't ask, so we don't pester them) and skip the
   * loading state for a cleaner background feel — the
   * auto-trigger only fires when the list is pristine, so the
   * worst case is the list stays empty and the user can click the
   * button explicitly.
   *
   * Replaces (not appends) by design: a regenerate is a fresh
   * answer, and surviving fragments would suggest the suggestion
   * is partially the user's old text.
   */
  const runSuggestQuestions = async (opts?: { auto?: boolean }) => {
    if (!knowledgeSource?.site) return;
    const auto = !!opts?.auto;
    setSuggestLoading(true);
    if (!auto) setSuggestNotice(null);
    try {
      const result = await suggestSampleQuestionsFromKB({
        siteId: knowledgeSource.site,
        // Send the full multi-folder selection so the prompt
        // reflects the union of the user's configured KB. Falls
        // back to the single legacy field for safety in case
        // folderPaths is empty.
        folderPaths:
          knowledgeSource.folderPaths && knowledgeSource.folderPaths.length > 0
            ? knowledgeSource.folderPaths
            : undefined,
        folderPath: knowledgeSource.folderPath || '',
        agentName: name.trim() || undefined,
        agentDescription: description.trim() || undefined,
        // One short of the cap so the user has obvious headroom to
        // add their own without immediately bumping into the limit.
        count: Math.max(1, MAX_SAMPLE_QUESTIONS - 1),
      });
      if (result.questions.length > 0) {
        setSampleQuestions(result.questions.slice(0, MAX_SAMPLE_QUESTIONS));
        setSuggestNotice({
          kind: 'success',
          text: `Generated ${result.questions.length} question${result.questions.length === 1 ? '' : 's'} from ${result.filenames.length} document${result.filenames.length === 1 ? '' : 's'}. Edit any of them to taste.`,
        });
      } else if (result.reason === 'no-indexable-documents') {
        if (!auto) {
          setSuggestNotice({
            kind: 'info',
            text: 'No indexable documents (.pdf, .doc/.docx, .xls/.xlsx, .csv, .htm/.html, .md) were found in the selected folder(s) or any subfolders. Add some documents, or pick a folder that has them.',
          });
        }
      } else if (result.reason === 'completion-failed') {
        if (!auto) {
          setSuggestNotice({
            kind: 'error',
            text: 'The suggestion service failed. Try again in a moment, or add your own questions below.',
          });
        }
      } else if (!auto) {
        setSuggestNotice({
          kind: 'info',
          text: 'No usable suggestions came back. Try renaming a few documents to be more descriptive, or write your own below.',
        });
      }
    } catch (err) {
      if (!auto) {
        setSuggestNotice({
          kind: 'error',
          text: err instanceof Error ? err.message : 'Suggestion request failed.',
        });
      }
    } finally {
      setSuggestLoading(false);
    }
  };

  const handleSuggestQuestions = () => {
    if (!canSuggestQuestions) return;
    void runSuggestQuestions({ auto: false });
  };

  // Whether the current sample-questions state is still "pristine"
  // (auto-replaceable). Pristine = empty, all-blank, or matches the
  // template seed verbatim. Once the user has edited even one
  // question to a non-template value we leave the list alone — the
  // auto-trigger should never silently overwrite real user work.
  // The separate `questionsTouched` flag covers add/remove actions
  // (which can produce a list shape that incidentally still equals
  // the template seed but represents intent).
  const sampleQuestionsArePristine = useMemo(() => {
    if (questionsTouched) return false;
    const trimmed = sampleQuestions.map((q) => q.trim()).filter((q) => q.length > 0);
    if (trimmed.length === 0) return true;
    const seed = (chosenTemplate?.sampleQuestions || [])
      .slice(0, MAX_SAMPLE_QUESTIONS)
      .map((q) => q.trim());
    if (seed.length !== trimmed.length) return false;
    return seed.every((q, i) => q === trimmed[i]);
  }, [sampleQuestions, chosenTemplate, questionsTouched]);

  // Auto-fire the suggestion call when the user picks (or changes)
  // a SharePoint source AND their list is still pristine. Debounced
  // so rapid folder navigation doesn't fan out. The cleanup cancels
  // an in-flight timer if the user keeps moving around. We
  // deliberately don't include `runSuggestQuestions` in the dep
  // array — it's recreated on every render and would re-fire the
  // effect on each keystroke in name/description; the function
  // closes over the latest values anyway because effects re-run
  // when its inputs change.
  // Stable string key for the multi-folder selection. Using the
  // array directly as a dep would re-fire the effect every render
  // because each onChange returns a new array reference even when
  // the contents are equivalent.
  const folderPathsKey = useMemo(
    () =>
      knowledgeSource?.folderPaths && knowledgeSource.folderPaths.length > 0
        ? knowledgeSource.folderPaths.join('|')
        : knowledgeSource?.folderPath || '',
    [knowledgeSource?.folderPaths, knowledgeSource?.folderPath]
  );

  useEffect(() => {
    if (!knowledgeSource?.site) return;
    if (!sampleQuestionsArePristine) return;
    if (suggestLoading) return;
    const handle = setTimeout(() => {
      void runSuggestQuestions({ auto: true });
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knowledgeSource?.site, folderPathsKey, sampleQuestionsArePristine]);

  const handleSubmit = async () => {
    if (!chosenTemplateId) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Multi-folder selection: send the full list as sourceFolders.
      // sourceFolder is kept as the primary (= folderPaths[0]) for
      // back-compat with the existing single-folder schema column;
      // the backend writes both onto the AIAgent record so older
      // consumers (status probe, reindex by-folder) keep working
      // until they migrate to sourceFolders.
      const allFolders =
        knowledgeSource?.folderPaths && knowledgeSource.folderPaths.length > 0
          ? knowledgeSource.folderPaths
          : knowledgeSource?.folderPath
            ? [knowledgeSource.folderPath]
            : [];

      // Folder-overlap pre-create guard (AA-210). If any existing
      // self-service agent already indexes one of the same (site,
      // folder) pairs, surface the conflict to the user before we
      // burn indexing budget on a duplicate. We don't block — the
      // user can confirm to proceed — but we want them to make an
      // informed choice rather than discover the duplicate three
      // weeks later via budget review.
      //
      // Implemented inline with window.confirm to match the codebase's
      // existing pattern for one-shot confirmations (see the
      // "Share with all SharePoint members" toggle); a modal would be
      // nicer UX but adds significant scope.
      if (knowledgeSource?.siteWebUrl && allFolders.length > 0) {
        try {
          const overlaps = await checkFolderOverlap(
            knowledgeSource.siteWebUrl,
            allFolders
          );
          if (overlaps.length > 0) {
            const lines = overlaps.slice(0, 5).map((o) => {
              const folderPart = o.overlappingFolders
                .map((f) => (f === '' ? '(library root)' : f))
                .join(', ');
              const ownerPart = o.ownerEmail ? ` — owned by ${o.ownerEmail}` : '';
              return `  • "${o.name}"${ownerPart}\n      folder${o.overlappingFolders.length === 1 ? '' : 's'}: ${folderPart}`;
            });
            if (overlaps.length > 5) {
              lines.push(`  …and ${overlaps.length - 5} more.`);
            }
            const ok = window.confirm(
              `The folder${allFolders.length === 1 ? '' : 's'} you picked ` +
                `${allFolders.length === 1 ? 'is' : 'are'} already indexed by ` +
                `${overlaps.length} other agent${overlaps.length === 1 ? '' : 's'}:\n\n` +
                lines.join('\n\n') +
                '\n\n' +
                'Creating another agent on the same folder doubles indexing ' +
                'cost and means there will be two agents answering questions ' +
                'about the same content. Continue anyway?'
            );
            if (!ok) {
              setSubmitting(false);
              return;
            }
          }
        } catch (err) {
          // Soft check: never block creation on a failed overlap lookup.
          // Log and proceed.
          // eslint-disable-next-line no-console
          console.warn('Folder overlap check failed; proceeding with create', err);
        }
      }

      const agent = await createMyAgent({
        templateId: chosenTemplateId,
        name: name.trim(),
        description: description.trim() || undefined,
        // agentId intentionally omitted — server-generated via
        // routes/myAgents.js _generateAgentId. Wizard no longer
        // exposes the slug; the field is admin-only now.
        sourceFolder: allFolders[0] ?? undefined,
        sourceFolders: allFolders.length > 0 ? allFolders : undefined,
        sourceSite: knowledgeSource?.site || undefined,
        sourceSiteWebUrl: knowledgeSource?.siteWebUrl || undefined,
        // Always Private at creation. The owner can invite specific
        // teammates from the My Agents card (which flips the agent to
        // Shared). Promotion to Published is admin-only.
        visibility: 'private',
        sampleQuestions: cleanedSampleQuestions.length > 0 ? cleanedSampleQuestions : undefined,
        routingTips: routingTips.trim() || undefined,
      });
      navigate(`/my-agents?created=${encodeURIComponent(agent.agentId)}`);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to create agent');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      // Outer scroll container — the AppContent main area is overflow-hidden,
      // so without this the wizard's footer (Next/Create button) is clipped.
      style={{
        height: '100%',
        overflowY: 'auto',
        background: 'var(--bg-primary, #f8f9fa)',
      }}
    >
    <div
      style={{
        maxWidth: 880,
        margin: '0 auto',
        padding: '32px 24px 64px',
        color: 'var(--text-primary, #1f2124)',
      }}
    >
      <div style={{ marginBottom: 24 }}>
        <button
          type="button"
          onClick={() => navigate('/my-agents')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: 'transparent',
            border: 'none',
            color: 'var(--brand-600, #047857)',
            cursor: 'pointer',
            padding: 0,
            fontSize: 13,
            marginBottom: 8,
          }}
        >
          <ArrowLeft size={14} /> Back to My Agents
        </button>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>Build an Agent</h1>
        <p style={{ margin: '6px 0 0', color: 'var(--text-secondary, #53575d)' }}>
          Spin up a personal AI assistant from a template. New agents are Private to start —
          you can invite specific teammates from the My Agents card afterwards.
        </p>
      </div>

      {/* Step indicator */}
      <ol
        style={{
          display: 'flex',
          gap: 8,
          listStyle: 'none',
          padding: 0,
          margin: '0 0 24px',
          flexWrap: 'wrap',
        }}
      >
        {visibleSteps.map((label, idx) => (
          <li
            key={label}
            style={{
              padding: '6px 12px',
              borderRadius: 999,
              background:
                idx === step
                  ? 'var(--brand-600, #047857)'
                  : idx < step
                    ? 'var(--brand-100, #d1fae5)'
                    : 'var(--bg-secondary, #f1f2f4)',
              color: idx === step ? '#fff' : idx < step ? 'var(--brand-800, #065f46)' : '#6f747b',
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            {idx + 1}. {label}
          </li>
        ))}
      </ol>

      {/* Step body */}
      <div
        style={{
          background: 'var(--bg-card, #fff)',
          border: '1px solid var(--border, #e3e5e8)',
          borderRadius: 12,
          padding: 24,
          minHeight: 320,
        }}
      >
        {visibleSteps[step] === 'Template' && (
          <div>
            <h2 style={{ marginTop: 0, fontSize: 20, fontWeight: 600 }}>Pick a template</h2>
            <p style={{ color: 'var(--text-secondary, #53575d)', fontSize: 14 }}>
              Templates set up the system prompt, tone, and citation behavior for you. You can
              customize anything later.
            </p>
            {templatesLoading && <div style={{ padding: 16 }}>Loading templates…</div>}
            {templatesError && (
              <div
                style={{
                  padding: 12,
                  color: '#991b1b',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: 6,
                }}
              >
                <AlertCircle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                {templatesError}
              </div>
            )}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                gap: 12,
                marginTop: 16,
              }}
            >
              {templates.map((tpl) => {
                const selected = tpl.id === chosenTemplateId;
                return (
                  <button
                    key={tpl.id}
                    type="button"
                    onClick={() => setChosenTemplateId(tpl.id)}
                    style={{
                      textAlign: 'left',
                      padding: 14,
                      borderRadius: 10,
                      border: selected
                        ? '2px solid var(--brand-600, #047857)'
                        : '1px solid var(--border, #e3e5e8)',
                      background: selected ? 'var(--brand-50, #ecfdf5)' : 'var(--bg-card, #fff)',
                      cursor: 'pointer',
                      transition: 'border-color 120ms, background 120ms',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 22 }}>{tpl.icon || '🤖'}</span>
                      <strong style={{ fontSize: 15 }}>{tpl.name}</strong>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary, #53575d)' }}>
                      {tpl.description}
                    </div>
                    {tpl.sampleQuestions && tpl.sampleQuestions.length > 0 && (
                      <div
                        style={{
                          fontSize: 12,
                          color: '#6f747b',
                          marginTop: 8,
                          fontStyle: 'italic',
                        }}
                      >
                        e.g. “{tpl.sampleQuestions[0]}”
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {visibleSteps[step] === 'Details' && (
          <div>
            <h2 style={{ marginTop: 0, fontSize: 20, fontWeight: 600 }}>Name your agent</h2>
            <label style={{ display: 'block', marginTop: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Agent name *</div>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Onboarding Buddy"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid var(--border, #cfd2d6)',
                  fontSize: 14,
                }}
              />
            </label>
            <label style={{ display: 'block', marginTop: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Short description</div>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={
                  chosenTemplate?.description ||
                  'What is this agent for? Helps teammates pick the right one.'
                }
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid var(--border, #cfd2d6)',
                  fontSize: 14,
                  resize: 'vertical',
                }}
              />
            </label>
          </div>
        )}

        {visibleSteps[step] === 'Knowledge' && (
          <div>
            <h2 style={{ marginTop: 0, fontSize: 20, fontWeight: 600 }}>Pick a knowledge source</h2>
            <p style={{ color: 'var(--text-secondary, #53575d)', fontSize: 14 }}>
              Search for a UniFirst SharePoint site you belong to, then pick one or more folders
              in its default document library. The agent will only answer from documents in
              those folders.
            </p>
            <SharePointFolderPicker value={knowledgeSource} onChange={setKnowledgeSource} />
            {knowledgeSource && (
              <>
                <div style={{ marginTop: 10, fontSize: 13, color: 'var(--brand-700, #047857)' }}>
                  <Check size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  Selected:{' '}
                  <code>
                    {knowledgeSource.siteName || knowledgeSource.siteWebUrl || knowledgeSource.site}
                    {knowledgeSource.folderPaths && knowledgeSource.folderPaths.length > 0
                      ? ` — ${knowledgeSource.folderPaths.length} folder${knowledgeSource.folderPaths.length === 1 ? '' : 's'}`
                      : knowledgeSource.folderPath
                        ? ` — ${knowledgeSource.folderPath}`
                        : ' — (library root)'}
                  </code>
                </div>
                {/* AA-210: agent is created Private. The Share tab on
                    the My Agents card lets the owner invite specific
                    people from this SharePoint site, or flip a single
                    "Share with all members of {SiteName}" toggle. */}
                <div
                  style={{
                    marginTop: 12,
                    padding: '10px 12px',
                    border: '1px solid var(--border, #e3e5e8)',
                    borderLeft: '3px solid var(--brand-600, #047857)',
                    background: 'var(--bg-secondary, #f8f9fa)',
                    borderRadius: 8,
                    fontSize: 12,
                    color: 'var(--text-primary, #1f2124)',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 2 }}>
                    Sharing happens after creation
                  </div>
                  <div style={{ color: '#6f747b' }}>
                    The agent is created Private to you. From the My Agents
                    page you can invite specific people from{' '}
                    <strong>{knowledgeSource.siteName || knowledgeSource.site || knowledgeSource.siteWebUrl}</strong>{' '}
                    or flip a single toggle to share with everyone who has
                    access to that site.
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {visibleSteps[step] === 'Routing' && (
          <div>
            <h2 style={{ marginTop: 0, fontSize: 20, fontWeight: 600 }}>
              Help Scout route to your agent
            </h2>
            <p style={{ color: 'var(--text-secondary, #53575d)', fontSize: 14, marginTop: 6 }}>
              These hints let Scout decide when to delegate to your agent instead of answering
              from a different specialist. You can leave everything blank — your agent will still
              be reachable via direct selection from the agent dropdown — but populating them
              meaningfully improves auto-routing.
            </p>

            <label style={{ display: 'block', marginTop: 20 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  marginBottom: 4,
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 500 }}>
                  Sample questions{' '}
                  <span style={{ color: '#6f747b', fontWeight: 400 }}>
                    ({cleanedSampleQuestions.length}/{MAX_SAMPLE_QUESTIONS})
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleSuggestQuestions}
                  disabled={!canSuggestQuestions}
                  title={
                    canSuggestQuestions
                      ? 'Regenerate questions from the documents in your connected SharePoint folder'
                      : knowledgeSource
                        ? 'Generating…'
                        : 'Pick a SharePoint folder in the Knowledge step first'
                  }
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 10px',
                    borderRadius: 6,
                    border: '1px solid var(--brand-600, #047857)',
                    background: canSuggestQuestions ? 'var(--brand-50, #ecfdf5)' : '#f1f2f4',
                    color: canSuggestQuestions ? 'var(--brand-700, #047857)' : '#9da2a8',
                    cursor: canSuggestQuestions ? 'pointer' : 'not-allowed',
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  {suggestLoading ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Sparkles size={14} />
                  )}
                  {suggestLoading
                    ? 'Generating…'
                    : cleanedSampleQuestions.length > 0
                      ? 'Regenerate from documents'
                      : 'Suggest from documents'}
                </button>
              </div>
              <div style={{ fontSize: 12, color: '#6f747b', marginBottom: 8 }}>
                Real questions a teammate might type. Scout compares incoming messages against
                these to decide whether to route here.
              </div>
              {suggestNotice && (
                <div
                  role="status"
                  style={{
                    fontSize: 12,
                    padding: '6px 10px',
                    borderRadius: 6,
                    marginBottom: 8,
                    border: '1px solid',
                    background:
                      suggestNotice.kind === 'success'
                        ? '#ecfdf5'
                        : suggestNotice.kind === 'error'
                          ? '#fef2f2'
                          : '#f1f2f4',
                    borderColor:
                      suggestNotice.kind === 'success'
                        ? '#10b981'
                        : suggestNotice.kind === 'error'
                          ? '#f87171'
                          : '#cfd2d6',
                    color:
                      suggestNotice.kind === 'success'
                        ? '#065f46'
                        : suggestNotice.kind === 'error'
                          ? '#991b1b'
                          : '#3d4045',
                  }}
                >
                  {suggestNotice.text}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {sampleQuestions.map((q, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 6 }}>
                    <input
                      type="text"
                      value={q}
                      onChange={(e) => {
                        const next = [...sampleQuestions];
                        next[idx] = e.target.value;
                        setSampleQuestions(next);
                        setQuestionsTouched(true);
                      }}
                      placeholder={`Sample question ${idx + 1}`}
                      style={{
                        flex: 1,
                        padding: '8px 10px',
                        borderRadius: 6,
                        border: '1px solid var(--border, #cfd2d6)',
                        fontSize: 13,
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setSampleQuestions(sampleQuestions.filter((_, i) => i !== idx));
                        setQuestionsTouched(true);
                      }}
                      title="Remove this question"
                      style={{
                        padding: '6px 10px',
                        borderRadius: 6,
                        border: '1px solid var(--border, #cfd2d6)',
                        background: 'transparent',
                        cursor: 'pointer',
                        color: '#6f747b',
                        fontSize: 13,
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                {sampleQuestions.length < MAX_SAMPLE_QUESTIONS && (
                  <button
                    type="button"
                    onClick={() => {
                      setSampleQuestions([...sampleQuestions, '']);
                      setQuestionsTouched(true);
                    }}
                    style={{
                      alignSelf: 'flex-start',
                      padding: '6px 12px',
                      borderRadius: 6,
                      border: '1px dashed var(--brand-600, #047857)',
                      background: 'transparent',
                      color: 'var(--brand-700, #047857)',
                      cursor: 'pointer',
                      fontSize: 13,
                    }}
                  >
                    + Add a sample question
                  </button>
                )}
              </div>
            </label>

            <label style={{ display: 'block', marginTop: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
                Routing tips
              </div>
              <div style={{ fontSize: 12, color: '#6f747b', marginBottom: 4 }}>
                One paragraph for Scout: when SHOULD it pick this agent, when should it NOT?
                Be specific about overlap with other specialists.
              </div>
              <textarea
                value={routingTips}
                onChange={(e) => setRoutingTips(e.target.value)}
                placeholder={
                  'Use for: questions about my team\'s onboarding documents.\n' +
                  'NOT for: company-wide HR policy (route to HR Policy instead).'
                }
                rows={4}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid var(--border, #cfd2d6)',
                  fontSize: 14,
                  resize: 'vertical',
                  fontFamily: 'inherit',
                }}
              />
            </label>

          </div>
        )}

        {visibleSteps[step] === 'Review' && (
          <div>
            <h2 style={{ marginTop: 0, fontSize: 20, fontWeight: 600 }}>Review &amp; create</h2>
            <dl style={{ marginTop: 12 }}>
              <Row label="Template" value={chosenTemplate?.name || '—'} />
              <Row label="Name" value={name || '—'} />
              <Row label="Description" value={description || '—'} />
              {requiresKnowledge && (
                <Row
                  label="Knowledge source"
                  value={(() => {
                    if (!knowledgeSource) return '(none yet)';
                    const site =
                      knowledgeSource.siteName ||
                      knowledgeSource.siteWebUrl ||
                      knowledgeSource.site;
                    const paths =
                      knowledgeSource.folderPaths && knowledgeSource.folderPaths.length > 0
                        ? knowledgeSource.folderPaths
                        : knowledgeSource.folderPath
                          ? [knowledgeSource.folderPath]
                          : [];
                    if (paths.length === 0) return `${site} — (library root)`;
                    if (paths.length === 1) return `${site} — ${paths[0] || '(library root)'}`;
                    return `${site} — ${paths.length} folders`;
                  })()}
                />
              )}
              <Row
                label="Sample questions"
                value={
                  cleanedSampleQuestions.length > 0
                    ? `${cleanedSampleQuestions.length} configured`
                    : '(none — falls back to template defaults)'
                }
              />
              <Row label="Routing tips" value={routingTips.trim() || '(none)'} />
              <Row
                label="Sharing"
                value="Private — invite teammates from the Share tab after creation"
              />
            </dl>

            {submitError && (
              <div
                style={{
                  marginTop: 12,
                  padding: 12,
                  color: '#991b1b',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: 6,
                  fontSize: 13,
                }}
              >
                <AlertCircle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                {submitError}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Nav */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 16,
          gap: 8,
        }}
      >
        <button
          type="button"
          onClick={handleBack}
          disabled={step === 0 || submitting}
          style={{
            padding: '10px 16px',
            borderRadius: 8,
            border: '1px solid var(--border, #cfd2d6)',
            background: 'transparent',
            cursor: step === 0 ? 'not-allowed' : 'pointer',
            color: 'var(--text-primary, #1f2124)',
            opacity: step === 0 ? 0.5 : 1,
          }}
        >
          <ArrowLeft size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Back
        </button>

        {step < visibleSteps.length - 1 ? (
          <button
            type="button"
            onClick={handleNext}
            disabled={!canAdvance}
            style={{
              padding: '10px 16px',
              borderRadius: 8,
              border: 'none',
              background: canAdvance ? 'var(--brand-600, #047857)' : '#9da2a8',
              color: '#fff',
              cursor: canAdvance ? 'pointer' : 'not-allowed',
              fontWeight: 500,
            }}
          >
            Next <ArrowRight size={14} style={{ verticalAlign: 'middle', marginLeft: 4 }} />
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              padding: '10px 16px',
              borderRadius: 8,
              border: 'none',
              background: submitting ? '#9da2a8' : 'var(--brand-600, #047857)',
              color: '#fff',
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontWeight: 500,
            }}
          >
            {submitting ? 'Creating…' : 'Create agent'}
          </button>
        )}
      </div>
    </div>
    </div>
  );
};

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: '160px 1fr',
      gap: 10,
      padding: '8px 0',
      borderBottom: '1px solid var(--border, #f1f2f4)',
    }}
  >
    <dt style={{ color: 'var(--text-secondary, #6f747b)', fontSize: 13 }}>{label}</dt>
    <dd style={{ margin: 0, fontSize: 14 }}>{value}</dd>
  </div>
);

export default BuildAnAgent;
